import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import { BRANDING } from "../../../config/branding";
import { generarPDFFactura } from "../../../utils/facturaPdf";
import logoPerfect from "../../../assets/Logo.png";
import {
  anularVenta,
  certificarFelVenta,
  getVentaDetalle,
  getVentasPendientes,
} from "../../../services/modules/PendientesAnulacion";

const fmtMoney = (n) => `Q${Number(n || 0).toFixed(2)}`;

const FelChip = ({ value }) => {
  const v = String(value || "").toUpperCase();
  const map = {
    PENDIENTE: { label: "PENDIENTE", color: "warning" },
    ERROR: { label: "ERROR", color: "error" },
    CERTIFICADO: { label: "CERTIFICADO", color: "success" },
    NO_APLICA: { label: "NO APLICA", color: "default" },
  };
  const cfg = map[v] || { label: v || "N/D", color: "default" };
  return <Chip size="small" label={cfg.label} color={cfg.color} variant={cfg.color === "default" ? "outlined" : "filled"} />;
};

export default function PendientesAnulacion() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ventaDetail, setVentaDetail] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [motivo, setMotivo] = useState("ANULACION");
  const [actionBusy, setActionBusy] = useState(false);
  const [logoImgEl, setLogoImgEl] = useState(null);

  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState("");

  const openPdfPreview = ({ url, filename }) => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(url);
    setPdfPreviewFilename(filename);
    setPdfPreviewOpen(true);
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl("");
    setPdfPreviewFilename("");
  };

  useEffect(() => () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
  }, [pdfPreviewUrl]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVentasPendientes({ limit: 100, offset: 0 });
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImgEl(img);
    img.src = logoPerfect;
  }, []);

  const openDetail = async (idVenta) => {
    setSelectedId(idVenta);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await getVentaDetalle(idVenta);
      setVentaDetail(data?.venta || null);
      setDetalles(Array.isArray(data?.detalles) ? data.detalles : []);
      setMotivo("ANULACION");
    } catch (e) {
      console.error(e);
      setVentaDetail(null);
      setDetalles([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id_venta", headerName: "Venta", sortable: true },
      {
        field: "fecha_venta",
        headerName: "Fecha",
        sortable: true,
        valueGetter: (_p, row) => String(row?.fecha_venta || "").replace("T", " ").slice(0, 16),
      },
      {
        field: "cliente_nombre",
        headerName: "Cliente",
        sortable: true,
        valueGetter: (_p, row) => row?.cliente_nombre || "CF",
      },
      {
        field: "total",
        headerName: "Total",
        sortable: true,
        align: "right",
        valueGetter: (_p, row) => fmtMoney(row?.total),
      },
      {
        field: "fel_estado",
        headerName: "FEL",
        renderCell: ({ row }) => <FelChip value={row?.fel_estado} />,
      },
      {
        field: "actions",
        headerName: "Acciones",
        renderCell: ({ row }) => (
          <Button size="small" variant="outlined" onClick={() => openDetail(row.id_venta)}>
            Ver
          </Button>
        ),
      },
    ],
    []
  );

  const canCertify =
    (ventaDetail?.fel_estado || "").toUpperCase() === "PENDIENTE" ||
    (ventaDetail?.fel_estado || "").toUpperCase() === "ERROR";

  const handleCertify = async () => {
    if (!selectedId) return;
    setActionBusy(true);
    try {
      const resFel = await certificarFelVenta(selectedId);
      // resFel: {exitoso, datos} o {exitoso:false,...}
      if (resFel?.exitoso) {
        const venta = {
          id_venta: selectedId,
          cliente: {
            nombre: ventaDetail?.cliente_nombre || "CF",
            nit: ventaDetail?.cliente_nit || "CF",
            direccion: ventaDetail?.cliente_direccion || "CIUDAD",
          },
          detalles: detalles.map((d) => ({
            id_producto: d.id_producto,
            codigo: d.codigo,
            descripcion: d.descripcion,
            cantidad: Number(d.cantidad || 0),
            precio_unitario: Number(d.precio_unitario || 0),
            total: Number(d.total || 0),
          })),
        };

        await generarPDFFactura(
          {
            ...venta,
            cliente: venta.cliente,
            detalles: venta.detalles,
            fechaVenta: ventaDetail?.fecha_venta,
          },
          resFel,
          logoImgEl,
          { save: false, onPreview: openPdfPreview, filename: `FEL_${selectedId}.pdf` }
        );

        await openDetail(selectedId);
        await refresh();
        return;
      }
      alert(JSON.stringify(resFel?.error || resFel));
      await openDetail(selectedId);
      await refresh();
    } catch (e) {
      console.error(e);
      alert("Error certificando FEL");
    } finally {
      setActionBusy(false);
    }
  };

  const handleAnular = async () => {
    if (!selectedId) return;
    const ok = window.confirm(`¿Anular venta ${selectedId} y reintegrar stock?`);
    if (!ok) return;
    setActionBusy(true);
    try {
      const res = await anularVenta(selectedId, { motivo, anularFel: true });
      alert(res?.message || "Venta anulada");
      setDetailOpen(false);
      setVentaDetail(null);
      setDetalles([]);
      await refresh();
    } catch (e) {
      console.error(e);
      alert("Error anulando venta");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <PageContainer>
      <Stack spacing={0.5}>
        <Typography variant="h5" fontWeight={800}>
          Pendientes / Anulación
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Certifica FEL pendiente o anula ventas reintegrando stock. Perfil: {BRANDING?.companyName}
        </Typography>
      </Stack>

      <AppDataTable rows={rows} columns={columns} loading={loading} />

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Venta {selectedId}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant="body2">Cargando...</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography fontWeight={700}>{ventaDetail?.cliente_nombre || "CF"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    NIT: {ventaDetail?.cliente_nit || "CF"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography fontWeight={800}>{fmtMoney(ventaDetail?.total)}</Typography>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", sm: "flex-end" }} mt={0.5}>
                    <FelChip value={ventaDetail?.fel_estado} />
                  </Stack>
                </Box>
              </Stack>

              {ventaDetail?.fel_error ? (
                <TextField
                  label="Error FEL"
                  value={String(ventaDetail.fel_error)}
                  fullWidth
                  multiline
                  minRows={3}
                  InputProps={{ readOnly: true }}
                />
              ) : null}

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ bgcolor: "background.default", px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Detalle
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  {detalles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sin detalle
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {detalles.map((d, idx) => (
                        <Stack key={idx} direction="row" spacing={1} justifyContent="space-between">
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {d.codigo ? `${d.codigo} • ` : ""}{d.descripcion}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Number(d.cantidad || 0)} x {fmtMoney(d.precio_unitario)} = {fmtMoney(d.total)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>

              <TextField
                label="Motivo de anulación"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleAnular}
            disabled={actionBusy || detailLoading}
          >
            Anular y reintegrar stock
          </Button>
          <Button
            variant="contained"
            onClick={handleCertify}
            disabled={actionBusy || detailLoading || !canCertify}
          >
            Certificar FEL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pdfPreviewOpen} onClose={closePdfPreview} fullWidth maxWidth="md">
        <DialogTitle>{pdfPreviewFilename || "Documento"}</DialogTitle>
        <DialogContent dividers sx={{ height: "80vh" }}>
          {pdfPreviewUrl ? (
            <iframe title="pdf-preview" src={pdfPreviewUrl} style={{ width: "100%", height: "100%", border: 0 }} />
          ) : (
            <Typography variant="body2">Sin vista previa</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdfPreview}>Cerrar</Button>
          <Button
            component="a"
            href={pdfPreviewUrl || undefined}
            download={pdfPreviewFilename || undefined}
            disabled={!pdfPreviewUrl}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
